import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class QuoteGenerator {
  constructor(quotes, containerId, buttonId, cloudsContainerId) {
    this.quotes = quotes;
    this.currentQuoteIndex = 0;
    this.quoteElement = document.getElementById(containerId);
    this.buttonElement = document.getElementById(buttonId);
    this.cloudsContainer = document.getElementById(cloudsContainerId);

    if (!this.quoteElement || !this.buttonElement || !this.cloudsContainer) {
      return;
    }

    // Get the section element (parent of clouds-container)
    this.sectionElement = this.cloudsContainer.closest("section");
    if (!this.sectionElement) {
      return;
    }

    // Hide clouds container initially.
    this.cloudsContainer.style.opacity = 0;

    // Initialize clouds and set up viewport fade-in
    this.initClouds();
    this.setupViewportFadeIn();
    this.showQuote(this.currentQuoteIndex);

    this.buttonElement.addEventListener("click", () => this.nextQuote());
  }

  setupViewportFadeIn() {
    // Fade in clouds-container when section enters viewport (from either direction)
    ScrollTrigger.create({
      trigger: this.sectionElement,
      start: "top 80%",
      onEnter: () => {
        gsap.to(this.cloudsContainer, { opacity: 1, duration: 0.5 });
      },
      onEnterBack: () => {
        gsap.to(this.cloudsContainer, { opacity: 1, duration: 0.5 });
      },
      onLeave: () => {
        gsap.to(this.cloudsContainer, { opacity: 0, duration: 0.5 });
      },
      onLeaveBack: () => {
        gsap.to(this.cloudsContainer, { opacity: 0, duration: 0.5 });
      },
    });
  }

  initClouds() {
    // Get all cloud elements.
    const clouds = document.querySelectorAll("#clouds-container .cloud");
    const cloudArray = Array.from(clouds);

    // Set all clouds to be visible and absolutely positioned initially.
    cloudArray.forEach((cloud) => {
      gsap.set(cloud, { opacity: 1, position: "absolute" });
    });

    if (cloudArray.length < 3) {
      console.warn("Not enough clouds for reappearance effect.");
      return;
    }

    // --- Separate the clouds into two groups ---
    // Random reappearing clouds: 3 randomly chosen.
    this.selectedRandomClouds = cloudArray.sort(() => Math.random() - 0.5).slice(0, 3);
    // Continuous clouds: all others.
    const continuousClouds = cloudArray.filter(
      (cloud) => !this.selectedRandomClouds.includes(cloud)
    );

    // --- Continuous Clouds ---
    // Apply a responsive fade‑out tween relative to section position.
    const mmFade = gsap.matchMedia();
    mmFade.add("(max-width: 767px)", () => {
      continuousClouds.forEach((cloud) => {
        gsap.to(cloud, {
          opacity: 0,
          scrollTrigger: {
            trigger: this.sectionElement,
            start: "top top",
            end: "+=400",
            scrub: true,
          },
          overwrite: "auto",
        });
      });
    });
    mmFade.add("(min-width: 768px) and (max-width: 1279px)", () => {
      continuousClouds.forEach((cloud) => {
        gsap.to(cloud, {
          opacity: 0,
          scrollTrigger: {
            trigger: this.sectionElement,
            start: "top top",
            end: "+=600",
            scrub: true,
          },
          overwrite: "auto",
        });
      });
    });
    mmFade.add("(min-width: 1280px)", () => {
      continuousClouds.forEach((cloud) => {
        gsap.to(cloud, {
          opacity: 0,
          scrollTrigger: {
            trigger: this.sectionElement,
            start: "top top",
            end: "+=800",
            scrub: true,
          },
          overwrite: "auto",
        });
      });
    });

    // --- Random Reappearing Clouds ---
    // Use matchMedia so that both the visible scroll range and scale are responsive.
    const mmRandom = gsap.matchMedia();
    mmRandom.add("(max-width: 767px)", () => {
      this.createRandomCloudTriggers(800, 0.5);
    });
    mmRandom.add("(min-width: 768px) and (max-width: 1279px)", () => {
      this.createRandomCloudTriggers(1000, 0.75);
    });
    mmRandom.add("(min-width: 1280px)", () => {
      this.createRandomCloudTriggers(1200, 1);
    });
  }

  createRandomCloudTriggers(visibleRange, scaleValue) {
    // Calculate even spacing based on the section's height.
    const sectionHeight = this.sectionElement.offsetHeight;
    const segment = sectionHeight / (this.selectedRandomClouds.length + 1);

    this.selectedRandomClouds.forEach((cloud, i) => {
      // Calculate a random (X, Y) position for this cloud.
      const randomX = Math.random() * (window.innerWidth - cloud.offsetWidth);
      const randomY = Math.random() * (window.innerHeight - cloud.offsetHeight);
      const triggerOffset = segment * (i + 1);

      // Ensure the cloud starts hidden.
      gsap.set(cloud, { opacity: 0, position: "absolute" });

      ScrollTrigger.create({
        trigger: this.sectionElement,
        start: `top+=${triggerOffset} top`,
        end: `top+=${triggerOffset + visibleRange} top`,
        scrub: true,
        onEnter: () => {
          // Set the cloud's random position when entering.
          gsap.set(cloud, { left: randomX, top: randomY });
          // Animate scale responsively.
          gsap.fromTo(
            cloud,
            { scale: 0 },
            { scale: scaleValue, duration: 0.5, ease: "back.out(2)" }
          );
        },
        onUpdate: (self) => {
          // Fade in for progress 0–0.5, then fade out.
          const prog = self.progress;
          const opacity = prog <= 0.5 ? prog * 2 : (1 - prog) * 2;
          gsap.set(cloud, { opacity });
        },
        onLeaveBack: () => {
          gsap.set(cloud, { opacity: 0 });
        },
      });
    });
  }

  showQuote(index) {
    const quote = this.quotes[index];
    const words = quote.split(" ");
    this.quoteElement.innerHTML = "";
    words.forEach((word) => {
      const span = document.createElement("span");
      span.textContent = word;
      span.style.display = "inline-block";
      span.style.marginRight = "2%";
      this.quoteElement.appendChild(span);
    });
    const spans = this.quoteElement.querySelectorAll("span");
    const timeline = gsap.timeline();
    spans.forEach((span, i) => {
      timeline.fromTo(
        span,
        { opacity: 0 },
        { opacity: 1, duration: 0.25, ease: "none" },
        i * 0.05
      );
      timeline.fromTo(
        span,
        { y: 8 },
        { y: 0, duration: 1.5, ease: "elastic.out" },
        i * 0.05
      );
    });
    // Start continuous cloud animations.
    this.animateClouds();
  }

  animateClouds() {
    // Animate only the continuous clouds (those NOT in the random reappearing group).
    const allClouds = Array.from(document.querySelectorAll("#clouds-container .cloud"));
    const continuousClouds = allClouds.filter(
      (cloud) => !this.selectedRandomClouds.includes(cloud)
    );

    const mmAnim = gsap.matchMedia();
    mmAnim.add("(max-width: 767px)", () => {
      runCloudAnimation(0.50, continuousClouds);
    });
    mmAnim.add("(min-width: 768px) and (max-width: 1279px)", () => {
      runCloudAnimation(0.75, continuousClouds);
    });
    mmAnim.add("(min-width: 1280px)", () => {
      runCloudAnimation(1, continuousClouds);
    });

    function runCloudAnimation(scaleValue, clouds) {
      clouds.forEach((cloud) => {
        // Randomly position each continuous cloud.
        const randomX = Math.random() * 90;
        const randomY = Math.random() * 90;
        gsap.set(cloud, {
          position: "absolute",
          left: `${randomX}%`,
          top: `${randomY}%`,
        });
        // Animate scale and vertical motion.
        gsap.fromTo(
          cloud,
          { scale: 0 },
          {
            scale: scaleValue,
            duration: 1,
            ease: "back.out(2)",
            immediateRender: false,
          }
        );
        gsap.to(cloud, {
          y: "+=20",
          duration: 4 + Math.random() * 2,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      });
    }
  }

  nextQuote() {
    this.currentQuoteIndex = (this.currentQuoteIndex + 1) % this.quotes.length;
    this.showQuote(this.currentQuoteIndex);
  }
}
